<template>
  <b-container>
    <b-row class="mt-4">
      <b-col>
        <Navigation
          targetPageValue="UserList"
          :targetPageName="$t('title.userManagement')"
          v-on:changePage="setNextPage"
        />
      </b-col>
    </b-row>
    <h1 class="text-center pt-4 pb-4">{{ $t("title.EditUser") }}</h1>

    <div v-if="!spinner">
      <b-form @submit="onSubmit">
        <b-row>
          <b-col sm class="mt-4">
            <b-form-input
              v-model.trim="$v.form.fname.$model"
              :placeholder="$t('label.fname')"
              :readonly="readonly"
            ></b-form-input>
            <div
              class="text-danger"
              v-if="!$v.form.fname.required && $v.form.fname.$dirty"
            >
              {{ $t("info.required") }}
            </div>
            <div
              class="text-danger"
              v-if="!$v.form.fname.maxLength && $v.form.fname.$dirty"
            >
              {{ $t("info.maxLength", [75]) }}
            </div>
          </b-col>
          <b-col sm class="mt-4">
            <b-form-input
              v-model.trim="$v.form.lname.$model"
              :placeholder="$t('label.lname')"
              :readonly="readonly"
            ></b-form-input>
            <div
              class="text-danger"
              v-if="!$v.form.lname.required && $v.form.lname.$dirty"
            >
              {{ $t("info.required") }}
            </div>
            <div
              class="text-danger"
              v-if="!$v.form.lname.maxLength && $v.form.lname.$dirty"
            >
              {{ $t("info.maxLength", [75]) }}
            </div>
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col sm class="mt-4">
            <b-form-input
              v-model.trim="$v.form.email.$model"
              :placeholder="$t('label.email')"
              readonly
            ></b-form-input>
            <div
              class="text-danger"
              v-if="!$v.form.email.required && $v.form.email.$dirty"
            >
              {{ $t("info.required") }}
            </div>
            <div
              class="text-danger"
              v-if="!$v.form.email.email && $v.form.email.$dirty"
            >
              {{ $t("info.email") }}
            </div>
            <div
              class="text-danger"
              v-if="!$v.form.email.maxLength && $v.form.email.$dirty"
            >
              {{ $t("info.maxLength", [75]) }}
            </div>
          </b-col>
          <b-col sm class="mt-4">
            <b-form-select
              v-model="form.role"
              :options="roles"
              :disabled="readonly"
            ></b-form-select>
            <div
              class="text-danger"
              v-if="!$v.form.role.required && $v.form.role.$dirty"
            >
              {{ $t("info.required") }}
            </div>
          </b-col>
        </b-row>

        <b-row
          v-if="buildings.length > 0 && (form.role === 1 || form.role === 5 || form.role === 6)"
          class="mt-2"
        >
          <b-col sm="6">
            <div class="mt-2">
              <b-alert show variant="info">
                <div>
                  <strong>{{ $t("info.buildingForUser") }}</strong>
                </div>

                <div class="mt-2" v-if="buildingList.length > 0">
                  <ul>
                    <li v-for="item in buildingList" :key="item.Id">
                      {{ item.NOMDIV }}
                    </li>
                  </ul>
                  <b-button
                    variant="link"
                    @click="resetMultiSelectList('buildingList')"
                    >{{ $t("action.resetTheList") }}</b-button
                  >
                </div>

                <div class="mt-4">
                  <b-button
                    variant="outline-info"
                    @click="openmodal('multiSelectTable')"
                    >{{ $t("action.addBuildingForUser") }}</b-button
                  >
                </div>
              </b-alert>
            </div>
          </b-col>
        </b-row>

        <b-modal
          id="multiSelectTable"
          size="xl"
          :title="$t('action.addBuildingForUser')"
          hide-footer
        >
          <MultiSelectTable
            ref="multiSelectBuilding"
            :tableDataFromProp="buildings"
            :fields="fieldsForMultipleSelectTable"
            tableName="buildingList"
          />
        </b-modal>

        <b-modal id="deleteModal" :title="$t('title.confirmation')" hide-footer>
          <DeleteUserConfirmation :values="form" modalid="deleteModal" v-on:changePage="setNextPage"/>
        </b-modal>

        <div>
          <b-button
            v-if="!readonly"
            class="mt-4"
            type="submit"
            variant="success"
            :disabled="disabled"
            >{{ $t("action.submit") }}</b-button
          >
          <b-button
            v-if="!readonly"
            class="mt-4 float-right"
            variant="danger"
            @click="openmodal('deleteModal')"
            >{{ $t("action.remove") }}</b-button
          >
        </div>
      </b-form>
    </div>

    <div v-if="spinner" class="text-center pt-2">
      <b-spinner
        style="width: 3rem; height: 3rem"
        variant="success"
        label="Spinning"
        disabled="labelStatus"
      ></b-spinner>
    </div>

    <div v-if="error" class="pt-4">
      <b-alert show variant="danger">
        <p>{{ error }}</p>
      </b-alert>
    </div>
  </b-container>
</template>

<script>
import axios from "axios";
import DeleteUserConfirmation from "./DeleteUserConfirmation.vue";
import { required, email, maxLength } from "vuelidate/lib/validators";
import MultiSelectTable from "../MultipleSelectTable.vue";
import Navigation from "../../Navigation.vue";

import { globalF } from "../../Helpers";

export default {
  mixins: [globalF],
  data() {
    return {
      form: {
        Id: this.getData("Id", ""),
        fname: this.getData("fname", ""),
        lname: this.getData("lname", ""),
        email: this.getData("email", ""),
        role: this.getData("role", ""),
        env: "prod",
      },
      error: "",
      disabled: false,
      spinner: false,
    };
  },
  validations() {
    return {
      form: {
        fname: {
          required,
          maxLength: maxLength(75),
        },
        lname: {
          required,
          maxLength: maxLength(75),
        },
        email: {
          required,
          email,
          maxLength: maxLength(75),
        },
        role: {
          required,
        },
        env: {
          required,
        },
      },
    };
  },
  mounted() {
    //this.$root.$on("bv::modal::hide", () => {});
  },
  computed: {
    url: function () {
      let urls = this.$store.getters.getUrlsList;
      return urls[process.env.VUE_APP_WHERE].json;
    },
    roles: function () {
      let roles = this.getGlobalData("roles");
      let selectRoles = roles.map((r) => {
        return {
          text: r.en,
          value: r.Id,
        };
      });
      selectRoles.unshift({ value: null, text: "Role" });
      return selectRoles;
    },
    readonly: function () {
      let user = this.$store.getters.getUserToEdit;
      return user.role === 4;
    },
    buildings: function () {
      return this.$store.getters.getBuildingsList;
    },
    buildingList: function () {
      return this.$store.getters.getBuildingListForUser;
    },
    fieldsForMultipleSelectTable: function () {
      return [
        { key: "NOCIE", sortable: true, label: "NOCIE" },
        { key: "NODIV", sortable: true, label: "NODIV" },
        { key: "NOMDIV", sortable: true, label: "NOMDIV" },
        { key: "Creation", sortable: true, label: this.$t("label.date") },
        { key: "details", label: this.$t("label.detail") },
      ];
    },
  },
  methods: {
    getData: function (type, propertyValue) {
      let user = this.$store.getters.getUserToEdit;
      if (user) {
        propertyValue = user[type];
      }
      return propertyValue;
    },
    openmodal: function (id) {
      this.$bvModal.show(id);
    },
    onSubmit(evt) {
      evt.preventDefault();
      this.emailexist = false;
      this.error = "";
      this.$v.$touch();
      if (!this.$v.$invalid) {
        /*Ajax Call*/
        this.spinner = true;
        this.form.buildingList = this.buildingList;

        let promise = this.validateToken();
        promise.then(() => {
          axios
            .post(
              this.url,
              { request: "editUser", form: this.form },
              { headers: { "Content-Type": "application/json" } }
            )
            .then(() => {
              this.$emit("changePage", "UserList");
            })
            .catch(() => {
              this.spinner = false;
              this.error = this.$t("info.errorgeneric");
              this.disabled = false;
            });
        });
      }
    },
    resetMultiSelectList: function (listName) {
      if (listName === "buildingList") {
        this.$store.commit("addBuildingListForUser", []);
      }
    },
    resetField: function () {
      this.$v.$reset();
      this.form.fname = "";
      this.form.lname = "";
      this.form.building = [];
      this.form.role = null;
      this.readonly = false;
    },
    setNextPage(page) {
      this.$emit("changePage", page);
    },
  },
  components: {
    DeleteUserConfirmation,
    MultiSelectTable,
    Navigation,
  },
};
</script>
